import React, { useRef, useEffect, useState, useMemo } from 'react'
import * as d3 from 'd3'

const styles = {
  card: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
    margin: 0,
  },
  subtitle: {
    fontSize: '13px',
    color: '#6b7280',
    margin: '4px 0 16px 0',
  },
  svgContainer: {
    width: '100%',
    maxWidth: '700px',
    margin: '0 auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#1f2937',
    color: '#f9fafb',
    padding: '10px 14px',
    borderRadius: '6px',
    fontSize: '12px',
    lineHeight: '1.5',
    zIndex: 9999,
    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
    maxWidth: '260px',
  },
  tooltipLabel: {
    fontWeight: 600,
    fontSize: '13px',
    marginBottom: '4px',
  },
  tooltipRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
  },
  tooltipKey: {
    color: '#9ca3af',
  },
  tooltipValue: {
    fontWeight: 500,
  },
  sparklineContainer: {
    marginTop: '6px',
    borderTop: '1px solid #374151',
    paddingTop: '6px',
  },
  sparklineLabel: {
    fontSize: '10px',
    color: '#9ca3af',
    marginBottom: '2px',
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    margin: '20px 0 10px 0',
  },
  listsContainer: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
  },
  listColumn: {
    flex: '1 1 280px',
    minWidth: '260px',
  },
  listTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '8px',
  },
  miniCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    marginBottom: '6px',
    background: '#f9fafb',
    fontSize: '12px',
  },
  miniCardLabel: {
    fontWeight: 500,
    color: '#111827',
    marginRight: '8px',
  },
  miniCardStats: {
    display: 'flex',
    gap: '10px',
    color: '#6b7280',
    fontSize: '11px',
    whiteSpace: 'nowrap',
  },
  stableBadge: {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 600,
    background: '#d1fae5',
    color: '#065f46',
  },
  volatileBadge: {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 600,
    background: '#fee2e2',
    color: '#991b1b',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#9ca3af',
    fontSize: '14px',
  },
  quadrantLabel: {
    fontSize: '10px',
    fill: '#9ca3af',
    fontWeight: 500,
  },
}

function buildSparklinePath(trace, width, height) {
  if (!trace || trace.length < 2) return ''
  const xScale = d3.scaleLinear().domain([0, trace.length - 1]).range([0, width])
  const yScale = d3.scaleLinear().domain([d3.min(trace), d3.max(trace)]).range([height, 0])
  const line = d3.line()
    .x((_, i) => xScale(i))
    .y(d => yScale(d))
    .curve(d3.curveMonotoneX)
  return line(trace)
}

export default function RankStabilityChart({ data }) {
  const svgRef = useRef(null)
  const [tooltip, setTooltip] = useState(null)

  const hasData = data && data.addresses && data.addresses.length > 0

  const { medianRank, medianStability, mostVolatile, mostStable } = useMemo(() => {
    if (!hasData) return { medianRank: 0, medianStability: 50, mostVolatile: [], mostStable: [] }

    const addresses = data.addresses
    const ranks = addresses.map(a => a.avgRank).sort((a, b) => a - b)
    const stabilities = addresses.map(a => a.stabilityScore).sort((a, b) => a - b)

    const medRank = d3.median(ranks)
    const medStab = d3.median(stabilities)

    const sorted = [...addresses].sort((a, b) => a.stabilityScore - b.stabilityScore)
    const volatile5 = sorted.slice(0, 5)
    const stable5 = sorted.slice(-5).reverse()

    return {
      medianRank: medRank,
      medianStability: medStab,
      mostVolatile: volatile5,
      mostStable: stable5,
    }
  }, [data, hasData])

  useEffect(() => {
    if (!hasData || !svgRef.current) return

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    const width = 700
    const height = 400
    const margin = { top: 30, right: 30, bottom: 50, left: 55 }
    const innerWidth = width - margin.left - margin.right
    const innerHeight = height - margin.top - margin.bottom

    const addresses = data.addresses
    const rankTraces = data.rankTraces || {}

    const xExtent = d3.extent(addresses, d => d.avgRank)
    const xPad = (xExtent[1] - xExtent[0]) * 0.08 || 1
    const xScale = d3.scaleLinear()
      .domain([Math.max(0, xExtent[0] - xPad), xExtent[1] + xPad])
      .range([0, innerWidth])

    const yScale = d3.scaleLinear()
      .domain([0, 100])
      .range([innerHeight, 0])

    const maxJumpExtent = d3.extent(addresses, d => d.maxRankJump)
    const sizeScale = d3.scaleSqrt()
      .domain([maxJumpExtent[0] || 0, maxJumpExtent[1] || 1])
      .range([4, 16])

    const colorScale = d3.scaleSequential(d3.interpolateRdYlGn)
      .domain([0, 100])

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    // Grid lines
    g.append('g')
      .attr('class', 'grid-x')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale).ticks(6).tickSize(-innerHeight).tickFormat(''))
      .selectAll('line')
      .attr('stroke', '#f3f4f6')
    g.select('.grid-x .domain').remove()

    g.append('g')
      .attr('class', 'grid-y')
      .call(d3.axisLeft(yScale).ticks(5).tickSize(-innerWidth).tickFormat(''))
      .selectAll('line')
      .attr('stroke', '#f3f4f6')
    g.select('.grid-y .domain').remove()

    // Quadrant lines
    const medX = xScale(medianRank)
    const medY = yScale(medianStability)

    g.append('line')
      .attr('x1', medX).attr('x2', medX)
      .attr('y1', 0).attr('y2', innerHeight)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,3')

    g.append('line')
      .attr('x1', 0).attr('x2', innerWidth)
      .attr('y1', medY).attr('y2', medY)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,3')

    // Quadrant labels
    const quadrants = [
      { x: medX / 2, y: medY / 2, label: 'Stable High Rank' },
      { x: medX + (innerWidth - medX) / 2, y: medY / 2, label: 'Stable Low Rank' },
      { x: medX / 2, y: medY + (innerHeight - medY) / 2, label: 'Volatile High Rank' },
      { x: medX + (innerWidth - medX) / 2, y: medY + (innerHeight - medY) / 2, label: 'Volatile Low Rank' },
    ]

    quadrants.forEach(q => {
      g.append('text')
        .attr('x', q.x)
        .attr('y', q.y)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .style('font-size', '10px')
        .style('fill', '#d1d5db')
        .style('font-weight', 500)
        .style('pointer-events', 'none')
        .text(q.label)
    })

    // Axes
    const xAxis = g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale).ticks(6))
    xAxis.select('.domain').attr('stroke', '#d1d5db')
    xAxis.selectAll('line').attr('stroke', '#d1d5db')
    xAxis.selectAll('text').style('font-size', '11px').style('fill', '#6b7280')

    const yAxis = g.append('g')
      .call(d3.axisLeft(yScale).ticks(5))
    yAxis.select('.domain').attr('stroke', '#d1d5db')
    yAxis.selectAll('line').attr('stroke', '#d1d5db')
    yAxis.selectAll('text').style('font-size', '11px').style('fill', '#6b7280')

    // Axis labels
    g.append('text')
      .attr('x', innerWidth / 2)
      .attr('y', innerHeight + 40)
      .attr('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('fill', '#6b7280')
      .text('Average Rank')

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerHeight / 2)
      .attr('y', -42)
      .attr('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('fill', '#6b7280')
      .text('Stability Score')

    // Dots
    g.selectAll('.dot')
      .data(addresses)
      .join('circle')
      .attr('class', 'dot')
      .attr('cx', d => xScale(d.avgRank))
      .attr('cy', d => yScale(d.stabilityScore))
      .attr('r', d => sizeScale(d.maxRankJump))
      .attr('fill', d => colorScale(d.stabilityScore))
      .attr('fill-opacity', 0.75)
      .attr('stroke', d => d3.color(colorScale(d.stabilityScore)).darker(0.5))
      .attr('stroke-width', 1)
      .style('cursor', 'pointer')
      .on('mouseenter', (event, d) => {
        d3.select(event.currentTarget)
          .attr('fill-opacity', 1)
          .attr('stroke-width', 2)

        const trace = rankTraces[d.id] || []
        setTooltip({
          x: event.clientX,
          y: event.clientY,
          address: d,
          trace,
        })
      })
      .on('mousemove', (event) => {
        setTooltip(prev => prev ? { ...prev, x: event.clientX, y: event.clientY } : null)
      })
      .on('mouseleave', (event) => {
        d3.select(event.currentTarget)
          .attr('fill-opacity', 0.75)
          .attr('stroke-width', 1)
        setTooltip(null)
      })

  }, [data, hasData, medianRank, medianStability])

  if (!hasData) {
    return (
      <div style={styles.card}>
        <h3 style={styles.title}>Price Rank Stability</h3>
        <p style={styles.subtitle}>Address rank volatility and stability analysis</p>
        <div style={styles.emptyState}>No rank stability data available.</div>
      </div>
    )
  }

  return (
    <div style={styles.card}>
      <h3 style={styles.title}>Price Rank Stability</h3>
      <p style={styles.subtitle}>Address rank volatility and stability analysis</p>

      <div style={styles.svgContainer}>
        <svg
          ref={svgRef}
          viewBox="0 0 700 400"
          style={{ width: '100%', height: 'auto' }}
        />
      </div>

      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x + 14,
            top: tooltip.y - 10,
          }}
        >
          <div style={styles.tooltipLabel}>{tooltip.address.label}</div>
          <div style={styles.tooltipRow}>
            <span style={styles.tooltipKey}>Avg Rank</span>
            <span style={styles.tooltipValue}>{tooltip.address.avgRank}</span>
          </div>
          <div style={styles.tooltipRow}>
            <span style={styles.tooltipKey}>Std Dev</span>
            <span style={styles.tooltipValue}>{tooltip.address.rankStdDev.toFixed(1)}</span>
          </div>
          <div style={styles.tooltipRow}>
            <span style={styles.tooltipKey}>Max Jump</span>
            <span style={styles.tooltipValue}>{tooltip.address.maxRankJump}</span>
          </div>
          <div style={styles.tooltipRow}>
            <span style={styles.tooltipKey}>Stability</span>
            <span style={styles.tooltipValue}>{tooltip.address.stabilityScore}/100</span>
          </div>
          {tooltip.trace.length >= 2 && (
            <div style={styles.sparklineContainer}>
              <div style={styles.sparklineLabel}>Rank trace</div>
              <svg width="180" height="30" style={{ display: 'block' }}>
                <path
                  d={buildSparklinePath(tooltip.trace, 180, 28)}
                  fill="none"
                  stroke="#60a5fa"
                  strokeWidth="1.5"
                  transform="translate(0,1)"
                />
              </svg>
            </div>
          )}
        </div>
      )}

      <div style={styles.listsContainer}>
        <div style={styles.listColumn}>
          <div style={styles.listTitle}>
            <span style={styles.volatileBadge}>Most Volatile</span>
          </div>
          {mostVolatile.map(addr => (
            <div key={addr.id} style={styles.miniCard}>
              <span style={styles.miniCardLabel}>{addr.label}</span>
              <div style={styles.miniCardStats}>
                <span>Rank {addr.avgRank}</span>
                <span>SD {addr.rankStdDev.toFixed(1)}</span>
                <span>Score {addr.stabilityScore}</span>
              </div>
            </div>
          ))}
        </div>
        <div style={styles.listColumn}>
          <div style={styles.listTitle}>
            <span style={styles.stableBadge}>Most Stable</span>
          </div>
          {mostStable.map(addr => (
            <div key={addr.id} style={styles.miniCard}>
              <span style={styles.miniCardLabel}>{addr.label}</span>
              <div style={styles.miniCardStats}>
                <span>Rank {addr.avgRank}</span>
                <span>SD {addr.rankStdDev.toFixed(1)}</span>
                <span>Score {addr.stabilityScore}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
